import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from '@mantine/form';
import {
    TextInput,
    NumberInput,
    Select,
    Textarea,
    Button,
    Group,
    Box,
    Stack,
    Paper,
    Title,
    Text,
    Badge,
    Modal,
    Switch,
    Alert
} from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { MoneyNumberInput, SkeletonList } from '@/components/snippets';
import { IconPlus, IconAlertCircle } from '@tabler/icons-react';

import {
    useSubscription,
    useUpdateSubscription,
    useRemoveSubscriptionPayment,
    useDeleteSubscription,
    useAddSubscriptionPayment,
} from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';
import { SUBSCRIPTIONS_PATH } from '@/lib/navigation';
import { setWebTitle } from '@/lib/format-utils';
import { Subscription, EMPTY_SUBSCRIPTION } from '@/types/subscription-models';
import MoneyTransactionsTable from '@/components/money-transactions-table';
import { useDisclosure } from '@mantine/hooks';
import NewPaymentOutModal from '@/components/new-payment-out-modal';
import { localTenant } from '@/services/session-service';
import CurrencySelector from '@/components/currency-selector';


export const EditSubscriptionPage = () => {
    const { t } = useTranslation();
    const tenant = localTenant();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [opened, { open, close }] = useDisclosure(false);

    const { data: subscription, isLoading } = useSubscription(id!);
    const updateMutation = useUpdateSubscription();
    const removePaymentMutation = useRemoveSubscriptionPayment();
    const deleteSubscriptionMutation = useDeleteSubscription();
    const addPaymentMutation = useAddSubscriptionPayment();

    const form = useForm<any>({
        initialValues: EMPTY_SUBSCRIPTION,
        validate: {
            name: (value) => (value.length < 2 ? t('field_required') : null),
            price: (value) => (value < 0 ? t('field_required') : null),
        },
    });

    useEffect(() => {
        if (subscription?.data) {
            form.setValues(subscription.data);
        }
    }, [subscription]);

    const handleSubmit = (values: Subscription) => {
        updateMutation.mutate({ ...values, id: Number(id) });
    };

    const handleDelete = () => {
        openConfirmModal({
            title: t('delete_recurring_expense'),
            children: <Text size="sm">{t('delete_recurring_expense_confirmation', { name: subscription?.data?.name })}</Text>,
            labels: { confirm: t('delete'), cancel: t('cancel') },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                deleteSubscriptionMutation.mutate(Number(id), {
                    onSuccess: () => {
                        notify.success(t('operation.success'), t('operation.delete_recurring_expense.success'));
                        navigate(SUBSCRIPTIONS_PATH);
                    },
                    onError: () => {
                        notify.error(t('operation.error'), t('operation.delete_recurring_expense.error'));
                    }
                });
            },
        });
    };

    const handleUnlinkPayment = (transactionId: number) => {
        if (id) {
            removePaymentMutation.mutate({ subscriptionId: id, moneyTransactionId: transactionId });
        }
    };

    setWebTitle(t('edit_subscription'));

    const hasPayments = subscription?.data?.payments && subscription.data.payments.length > 0;
    const isCanceled = form.values.status === 'canceled';
    const isReadOnly = subscription?.data?.can_edit === false;

    let statusColor = 'green';
    let statusText = t('status_active');

    if (form.values.status === 'overdue') {
        statusColor = 'red';
        statusText = t('status_overdue');
    } else if (form.values.status === 'canceled') {
        statusColor = 'gray';
        statusText = t('status_canceled');
    } else if (form.values.status === 'paused') {
        statusColor = 'yellow';
        statusText = t('status_paused');
    } else if (form.values.status === 'inactive') {
        statusColor = 'gray';
        statusText = t('status_inactive');
    }

    return (
        <div>
            <div className="panel-content max-w-3xl mx-auto">
                {isLoading ? (
                    <SkeletonList height={50} />
                ) : (
                    <>
                        <Paper p="lg" withBorder className="shadow-xs space-y-8">
                            {/* Header Section / Status */}
                            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                                <div>
                                    <h3 className="text-base font-bold text-gray-900">{t('recurring_expense_details')}</h3>
                                    <Text size="xs" c="dimmed">{t('recurring_expense_details_description')}</Text>
                                </div>
                                <Badge color={statusColor} variant="filled" size="sm">
                                    {statusText}
                                </Badge>
                            </div>

                            {isReadOnly && (
                                <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
                                    {t('subscription_readonly_warning')}
                                </Alert>
                            )}

                            {/* Section 1: General Details */}
                            <Stack gap="sm">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('general_information')}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <TextInput
                                        label={t('name')}
                                        placeholder={t('name')}
                                        required
                                        {...form.getInputProps('name')}
                                        disabled={isReadOnly}
                                    />
                                    <Select
                                        label={t('status')}
                                        data={[
                                            { value: 'active', label: t('status_active') },
                                            { value: 'paused', label: t('status_paused') },
                                            { value: 'canceled', label: t('status_canceled') },
                                        ]}
                                        {...form.getInputProps('status')}
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </Stack>

                            {/* Section 2: Billing & Costs */}
                            <Stack gap="sm" className="pt-2 border-t border-gray-100">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('billing_and_cost')}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Select
                                        label={t('cycle')}
                                        data={[
                                            { value: 'monthly', label: t('cycle_monthly') },
                                            { value: 'yearly', label: t('cycle_yearly') },
                                        ]}
                                        {...form.getInputProps('cycle')}
                                        disabled={isReadOnly}
                                    />

                                    <MoneyNumberInput
                                        label={t('price')}
                                        amount={form.values.price}
                                        onChange={(val) => form.setFieldValue('price', val)}
                                        disabled={isReadOnly}
                                    />

                                    <CurrencySelector
                                        label={t('currency')}
                                        className="w-full"
                                        value={form.values.currency}
                                        onChange={(val) => form.setFieldValue('currency', val || '--')}
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </Stack>

                            {/* Section 3: Attribution & Schedule */}
                            <Stack gap="sm" className="pt-2 border-t border-gray-100">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('attribution_and_schedule')}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Select
                                        label={t('recurring_expense_business')}
                                        placeholder={t('select_business')}
                                        data={tenant?.businesses?.map(b => ({ value: String(b.id), label: b.name })) || []}
                                        value={form.values.business_id ? String(form.values.business_id) : null}
                                        onChange={(value) => form.setFieldValue('business_id', value ? Number(value) : null)}
                                        disabled={isReadOnly}
                                    />
                                    <NumberInput
                                        label={t('due_day')}
                                        placeholder="1-31"
                                        min={1}
                                        max={31}
                                        {...form.getInputProps('due_day')}
                                        disabled={isReadOnly}
                                    />
                                    <Switch
                                        label={t('auto_payment')}
                                        checked={form.values.auto_payment}
                                        onChange={(e) => form.setFieldValue('auto_payment', e.currentTarget.checked)}
                                        className="font-semibold text-left"
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </Stack>

                            <Textarea
                                label={t('notes')}
                                placeholder={t('notes')}
                                minRows={3}
                                {...form.getInputProps('description')}
                                disabled={isReadOnly}
                            />

                            {/* Footer Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                {hasPayments ? (
                                    !isCanceled && (
                                        <Button
                                            variant="subtle"
                                            color="red"
                                            size="xs"
                                            onClick={() => {
                                                openConfirmModal({
                                                    title: t('cancel_recurring_expense'),
                                                    children: <Text size="sm">{t('cancel_recurring_expense_confirmation', { name: subscription?.data?.name })}</Text>,
                                                    labels: { confirm: t('confirm'), cancel: t('cancel') },
                                                    confirmProps: { color: 'red' },
                                                    onConfirm: () => {
                                                        updateMutation.mutate({ ...form.values, id: Number(id), status: 'canceled' }, {
                                                            onSuccess: () => {
                                                                notify.success(t('operation.success'), t('operation.cancel_recurring_expense.success'));
                                                                form.setValues({ status: 'canceled' } as any);
                                                            }
                                                        });
                                                    }
                                                });
                                            }}
                                            disabled={isReadOnly}
                                        >
                                            {t('cancel_subscription')}
                                        </Button>
                                    )
                                ) : (
                                    subscription?.data?.can_delete !== false && (
                                        <Button
                                            variant="subtle"
                                            color="red"
                                            size="xs"
                                            onClick={() => handleDelete()}
                                            disabled={isReadOnly}
                                        >
                                            {t('delete')}
                                        </Button>
                                    )
                                )}
                                {!isReadOnly && (
                                    <Button
                                        size="xs"
                                        color='black'
                                        onClick={() => form.onSubmit(handleSubmit)()}
                                        loading={updateMutation.isPending}
                                    >
                                        {t('save')}
                                    </Button>
                                )}
                            </div>
                        </Paper>

                        <Box mt="xl" className="border-t border-gray-100 pt-6">
                            <Group justify="space-between" align="center" mb="sm">
                                <div>
                                    <Title order={3} className="text-gray-900 text-lg font-bold">
                                        {t('payments')}
                                    </Title>
                                    <Text size="xs" c="dimmed" mt={2}>
                                        {t('payments_for_recurring_expense_description')}
                                    </Text>
                                </div>
                                <Button
                                    size="xs"
                                    color="black"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={open}
                                >
                                    {t('add_payment_for_this_expense')}
                                </Button>
                            </Group>

                            <MoneyTransactionsTable
                                moneyTransactions={subscription?.data?.payments || []}
                                onDelete={handleUnlinkPayment}
                            />
                        </Box>
                    </>
                )}

                {subscription?.data && opened && (
                    <Modal
                        opened={opened}
                        onClose={close}
                        title={t('add_payment_for_this_expense')}
                        size="lg"
                    >
                        <NewPaymentOutModal
                            onClose={close}
                            initialAmount={subscription.data.price}
                            initialCurrency={subscription.data.currency}
                            onSuccess={async (createdPayment) => {
                                close();
                                if (createdPayment?.id) {
                                    await addPaymentMutation.mutateAsync({
                                        subscriptionId: id!,
                                        moneyTransactionId: createdPayment.id
                                    });
                                }
                            }}
                        />
                    </Modal>
                )}
            </div>
        </div>
    );

};

export default EditSubscriptionPage;
